import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import type { BookingConfirmation } from '../types';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40; // ~2 minutos

export function useConfirmation(bookingId: string | null) {
  const [data, setData] = useState<BookingConfirmation | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      setError(new Error('Reserva não informada.'));
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        attemptsRef.current += 1;
        const res = await api.getConfirmation(bookingId);
        if (cancelled) return;
        setData(res);
        setLoading(false);

        const finalized =
          res.status === 'paid' ||
          res.status === 'failed' ||
          res.status === 'refunded' ||
          res.status === 'capacity_conflict' ||
          res.status === 'cancelled';

        if (!finalized && attemptsRef.current < MAX_POLL_ATTEMPTS) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        } else if (!finalized) {
          setTimedOut(true);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error('Erro ao consultar reserva'));
        setLoading(false);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [bookingId]);

  return { data, error, loading, timedOut };
}
